import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { seoPageFor } from '../../content/seoPages';
import { PUBLIC_PAGES, SITE, NAME } from '../../content/publicPages';
import { trackPageView } from '../../utils/analytics';

// The site is a single-page app, so index.html is the only page Google is given and every address would carry the home page's title,
// description and canonical link. This sets the right ones for each address, and marks private pages "noindex" (do not show in Google).
// A page may still set a better title of its own once its data has loaded (blog posts and service pages do).

// pages that need a login or are private: they must not appear in Google
const PRIVATE_PAGES = [
    ['/dashboard', 'Member Dashboard'],
    ['/service-provider', 'Service Provider Dashboard'],
    ['/admin', 'Admin'],
    ['/messages', 'Messages'],
    ['/notifications', 'Notifications'],
    ['/language', 'Choose Language'],
    ['/profile/', 'Profile'],
];

// pages that exist for any value in the address, with a title that a page replaces once it has loaded its data
const DYNAMIC = [
    { prefix: '/blog/', title: 'Blog', description: PUBLIC_PAGES['/blog'][1] },
    { prefix: '/services/', title: 'Wedding Service', description: PUBLIC_PAGES['/services'][1] },
];

let defaults = null;
const readDefaults = () => {
    if (defaults) return defaults;
    const content = selector => document.head.querySelector(selector)?.getAttribute('content') || '';
    defaults = {
        title: document.title,
        description: content('meta[name="description"]'),
        ogTitle: content('meta[property="og:title"]'),
        ogDescription: content('meta[property="og:description"]'),
        twitterTitle: content('meta[name="twitter:title"]'),
        twitterDescription: content('meta[name="twitter:description"]'),
    };
    return defaults;
};

const setTag = (selector, create, attribute, value) => {
    let element = document.head.querySelector(selector);
    if (!element) { element = create(); document.head.appendChild(element); }
    element.setAttribute(attribute, value);
};
const setMeta = (key, keyValue, content) => setTag(`meta[${key}="${keyValue}"]`, () => { const m = document.createElement('meta'); m.setAttribute(key, keyValue); return m; }, 'content', content);
const setCanonical = href => setTag('link[rel="canonical"]', () => { const l = document.createElement('link'); l.setAttribute('rel', 'canonical'); return l; }, 'href', href);

export const seoFor = pathname => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (path === '/') return { path, home: true, noindex: false };
    const privatePage = PRIVATE_PAGES.find(([prefix]) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix));
    if (privatePage) return { path, title: privatePage[1], noindex: true };
    if (PUBLIC_PAGES[path]) return { path, title: PUBLIC_PAGES[path][0], description: PUBLIC_PAGES[path][1], noindex: false };
    const landing = seoPageFor(path);
    if (landing) return { path, title: landing.metaTitle, description: landing.description, noindex: false };
    const dynamic = DYNAMIC.find(item => path.startsWith(item.prefix) && path.length > item.prefix.length);
    if (dynamic) return { path, title: dynamic.title, description: dynamic.description, noindex: false, dynamic: true };
    return { path, noindex: true }; // an address that does not exist (the "page not found" screen)
};

export default function RouteSeo() {
    const { pathname } = useLocation();

    useEffect(() => {
        const base = readDefaults();
        const seo = seoFor(pathname);
        const canonical = `${SITE}${seo.path === '/' ? '/' : seo.path}`;

        // the brand name is added only when the whole title still fits in a search result (about 60 characters)
        const withBrand = seo.title ? `${seo.title} | ${NAME}` : NAME;
        const title = seo.home ? base.title : seo.title && withBrand.length > 64 ? seo.title : withBrand;
        const description = seo.home ? base.description : seo.description || base.description;
        document.title = title;
        setMeta('name', 'description', description);
        setMeta('property', 'og:title', seo.home ? base.ogTitle : title);
        setMeta('property', 'og:description', seo.home ? base.ogDescription : description);
        setMeta('property', 'og:url', canonical);
        setMeta('name', 'twitter:title', seo.home ? base.twitterTitle : title);
        setMeta('name', 'twitter:description', seo.home ? base.twitterDescription : description);
        setCanonical(canonical);
        setMeta('name', 'robots', seo.noindex
            ? 'noindex, nofollow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

        // one page view per page, now that the title is right. Blog posts and service pages send theirs themselves once their own title has loaded.
        if (!seo.dynamic) trackPageView();
    }, [pathname]);

    return null;
}
